// Meal Results — Supabase-authoritative selection, swap, side inclusion.
//
// Two source-of-truth branches:
//   1. Selected plan (authoritative) — comes from get_selected_meal_plan
//      and from the fresh day contract returned by swap_meal_plan_item /
//      set_meal_plan_side_included.
//   2. Unselected generated options — the three plans returned by
//      generate_meal_plans (cached in sessionStorage under
//      etff.generation_result.v1) so the customer can Select This Plan.
//
// The browser never mutates meal_plans or meal_plan_items directly and
// never re-computes nutrition when the RPC has returned fresh totals.

import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandButton } from '../components/BrandButton';
import { SwapMealPicker } from '../components/SwapMealPicker';
import { useSession } from '../lib/useSession';
import {
  readCachedGenerationResult,
  clearCachedGenerationResult,
  startNewGenerationAttempt,
} from '../lib/mealGeneration';
import {
  readGenerationId as readGenGenerationId,
  readOutcome as readGenOutcome,
  readPlans,
  readPlanId,
  readPlanLabel,
  readPlanExplanation,
  readPlanItemsByDay,
  PLAN_LABEL_DISPLAY,
} from '../lib/mealPlanReader';
import {
  rpcSelectMealPlan,
  rpcGetSelectedMealPlan,
  rpcSwapMealPlanItem,
  rpcSetMealPlanSideIncluded,
  readSelectedPlan,
  readAffectedDay,
  readSelectedPlanOutcome,
  hasSelectedPlan,
  isSuccessfulSelect,
  isSuccessfulSwap,
  isSuccessfulSideChange,
} from '../lib/planPersistence';
import { describeGap, gapColorFor } from '../lib/targetGapWording';

function fmtInt(n) { return Number.isFinite(n) ? Math.round(n).toString() : '—'; }
function fmtDec1(n) { return Number.isFinite(n) ? (Math.round(n * 10) / 10).toString() : '—'; }

function GapChip({ nutrient, value }) {
  if (!Number.isFinite(value)) return null;
  const g = describeGap(nutrient, value);
  return (
    <span className="inline-block mr-3 mt-1 text-xs"
      style={{ color: gapColorFor(g.direction) }}
      data-testid={`gap-${nutrient}`}>
      {g.text}
    </span>
  );
}

function TotalCell({ eyebrow, value, testid }) {
  return (
    <div>
      <div className="eyebrow whitespace-nowrap"
        style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>
        {eyebrow}
      </div>
      <div className="font-display text-xl" data-testid={testid}>{value}</div>
    </div>
  );
}

// Day totals + gap chips rendered from Supabase-returned values.
function DayTotalsRow({ label, index, day }) {
  return (
    <>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-3"
        data-testid={`plan-day-totals-${label || index}-${day.day_number}`}>
        <TotalCell testid={`plan-day-total-calories-${label || index}-${day.day_number}`}
          eyebrow="Calories" value={fmtInt(day.totals.calories)} />
        <TotalCell testid={`plan-day-total-protein-${label || index}-${day.day_number}`}
          eyebrow="Protein" value={`${fmtDec1(day.totals.protein)} g`} />
        <TotalCell testid={`plan-day-total-carbs-${label || index}-${day.day_number}`}
          eyebrow="Carbs" value={`${fmtDec1(day.totals.carbohydrates)} g`} />
        <TotalCell testid={`plan-day-total-fat-${label || index}-${day.day_number}`}
          eyebrow="Fat" value={`${fmtDec1(day.totals.fat)} g`} />
      </div>

      {day.target_gaps && (
        <div className="mt-3" data-testid={`plan-day-gaps-${label || index}-${day.day_number}`}>
          <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>
            Compared to your targets
          </div>
          <div className="mt-1">
            <GapChip nutrient="calories" value={day.target_gaps.calories} />
            <GapChip nutrient="protein" value={day.target_gaps.protein} />
            <GapChip nutrient="carbohydrates" value={day.target_gaps.carbohydrates} />
            <GapChip nutrient="fat" value={day.target_gaps.fat} />
          </div>
        </div>
      )}
    </>
  );
}

function ItemRow({
  item, planKey, canAdjustSides, canSwap, onSwap, onToggleSide, submittingKey,
}) {
  const isSide = item.role !== 'core';
  const removed = item.customer_included === false;
  const key = [item.day_number, item.slot, item.component_order, item.etm_id].join('-');
  const allergenText = item.major_allergens
    || (Array.isArray(item.allergens) && item.allergens.length > 0 ? item.allergens.join(', ') : null);

  return (
    <div
      className="py-3 flex items-start justify-between gap-3 flex-wrap"
      style={{
        borderBottom: '1px solid var(--line)',
        opacity: removed ? 0.5 : 1,
      }}
      data-testid={`plan-item-${planKey}-${item.day_number}-${item.slot}-${item.component_order}`}
    >
      <div className="min-w-0" style={{ flex: '1 1 260px' }}>
        <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>
          {isSide ? `Add-on · ${item.role.replace('_', ' ')}` : `Meal ${item.slot}`}
          {item.etm_id ? ` · ${item.etm_id}` : ''}
        </div>
        <div
          className="font-display leading-tight"
          style={{
            color: 'var(--ink)',
            fontSize: isSide ? '1rem' : '1.15rem',
            textDecoration: removed ? 'line-through' : 'none',
          }}
          data-testid={`plan-item-name-${item.etm_id || key}`}
        >
          {isSide && '↳ '}
          {item.product_name || '—'}
          {item.quantity > 1 && (
            <span className="ml-2 text-sm" style={{ color: 'var(--text-dim)' }}>× {item.quantity}</span>
          )}
        </div>
        {item.serving_size && (
          <div className="mt-1 text-xs" style={{ color: 'var(--text-dim)' }}>
            Serving: {item.serving_size}
          </div>
        )}
        {allergenText && (
          <div className="mt-1 text-xs" style={{ color: 'var(--text-dim)' }}
            data-testid={`plan-item-allergens-${item.etm_id || key}`}>
            Contains: {allergenText}
          </div>
        )}
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          {canSwap && !isSide && (
            <button
              type="button"
              onClick={() => onSwap(item)}
              className="text-xs underline"
              style={{ color: 'var(--ink-2)' }}
              disabled={submittingKey === key}
              data-testid={`plan-item-swap-${item.etm_id || key}`}
            >
              {submittingKey === key ? 'Working…' : 'Swap this meal'}
            </button>
          )}
          {canAdjustSides && isSide && (
            <button
              type="button"
              onClick={() => onToggleSide(item)}
              className="text-xs underline"
              style={{ color: 'var(--ink-2)' }}
              disabled={submittingKey === key}
              data-testid={`plan-item-toggle-${item.etm_id || key}`}
            >
              {submittingKey === key
                ? 'Working…'
                : removed ? 'Add this side back' : 'Remove this side'}
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-x-3 gap-y-1 text-sm"
        style={{ color: 'var(--ink-2)', minWidth: 260 }}>
        <TotalCell eyebrow="Cal"
          value={fmtInt((item.calories ?? null) * (item.quantity || 1))}
          testid={`plan-item-cal-${item.etm_id || key}`} />
        <TotalCell eyebrow="Prot"
          value={`${fmtDec1((item.protein ?? null) * (item.quantity || 1))} g`}
          testid={`plan-item-prot-${item.etm_id || key}`} />
        <TotalCell eyebrow="Carb"
          value={`${fmtDec1((item.carbohydrates ?? null) * (item.quantity || 1))} g`}
          testid={`plan-item-carb-${item.etm_id || key}`} />
        <TotalCell eyebrow="Fat"
          value={`${fmtDec1((item.fat ?? null) * (item.quantity || 1))} g`}
          testid={`plan-item-fat-${item.etm_id || key}`} />
      </div>
    </div>
  );
}

function SelectedPlanCard({
  plan, onDeselect, onSwapItem, onToggleSide, submittingKey,
}) {
  const label = plan.option_label || '';
  const planKey = label || 'selected';
  const displayLabel = PLAN_LABEL_DISPLAY[label] || label || 'Your plan';

  return (
    <section
      className="mb-8 p-4"
      style={{
        border: '2px solid var(--orange, #d34a1e)',
        borderRadius: 'var(--radius)',
        background: 'var(--paper)',
        boxShadow: '0 4px 20px rgba(211,74,30,0.10)',
      }}
      data-testid={`plan-card-${planKey}`}
      data-selected="true"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0" style={{ flex: '1 1 280px' }}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Your selected plan</div>
            <span
              className="eyebrow px-2 py-1"
              style={{
                color: 'var(--paper)', background: 'var(--orange, #d34a1e)',
                borderRadius: 'var(--radius)', fontSize: '10px', letterSpacing: '0.14em',
              }}
              data-testid={`plan-selected-badge-${planKey}`}
            >
              Selected
            </span>
          </div>
          <h2 className="font-display text-2xl leading-tight mt-1"
            style={{ color: 'var(--ink)' }}
            data-testid={`plan-name-${planKey}`}>
            {displayLabel}
          </h2>
          {plan.explanation && (
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-2)' }}
              data-testid={`plan-explanation-${planKey}`}>
              {plan.explanation}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <BrandButton type="button" variant="outline" size="sm"
            onClick={onDeselect}
            data-testid={`plan-change-btn-${planKey}`}>
            Change plan
          </BrandButton>
        </div>
      </div>

      <div className="mt-6">
        {plan.days.map((day, i) => (
          <div key={day.day_number} className="mb-6">
            {plan.days.length > 1 && (
              <h3 className="font-display text-lg" style={{ color: 'var(--ink)' }}
                data-testid={`plan-day-title-${planKey}-${day.day_number}`}>
                Day {day.day_number}
              </h3>
            )}
            <DayTotalsRow label={label} index={i} day={day} />
            <div className="mt-4" style={{ borderTop: '1px solid var(--line)' }}>
              {day.items.map((it) => (
                <ItemRow
                  key={[it.day_number, it.slot, it.component_order, it.etm_id].join('-')}
                  item={it}
                  planKey={planKey}
                  canAdjustSides
                  canSwap
                  onSwap={onSwapItem}
                  onToggleSide={onToggleSide}
                  submittingKey={submittingKey}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {plan.plan_id && (
        <div className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}>
          Plan reference: <span data-testid={`plan-id-${planKey}`}>{plan.plan_id}</span>
        </div>
      )}
    </section>
  );
}

function OptionPlanCard({ plan, index, isSelecting, onSelect }) {
  const planId = readPlanId(plan);
  const label = readPlanLabel(plan) || '';
  const planKey = label || String(index);
  const displayLabel = PLAN_LABEL_DISPLAY[label] || label || `Option ${index + 1}`;
  const explanation = readPlanExplanation(plan);
  const daysList = readPlanItemsByDay(plan);

  return (
    <section
      className="mb-8 p-4"
      style={{
        border: '1px solid var(--line)', borderRadius: 'var(--radius)',
        background: 'var(--paper)',
      }}
      data-testid={`plan-card-${planKey}`}
      data-selected="false"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0" style={{ flex: '1 1 280px' }}>
          <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Option {index + 1}</div>
          <h2 className="font-display text-2xl leading-tight mt-1"
            style={{ color: 'var(--ink)' }}
            data-testid={`plan-name-${planKey}`}>
            {displayLabel}
          </h2>
          {explanation && (
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-2)' }}
              data-testid={`plan-explanation-${planKey}`}>
              {explanation}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <BrandButton
            type="button" variant="primary" size="sm"
            onClick={() => onSelect(planId, label)}
            disabled={isSelecting}
            data-testid={`plan-select-btn-${planKey}`}>
            {isSelecting ? 'Saving…' : 'Select this plan'}
          </BrandButton>
        </div>
      </div>

      <div className="mt-6">
        {daysList.map((day, i) => (
          <div key={day.day_number} className="mb-6">
            {daysList.length > 1 && (
              <h3 className="font-display text-lg" style={{ color: 'var(--ink)' }}
                data-testid={`plan-day-title-${planKey}-${day.day_number}`}>
                Day {day.day_number}
              </h3>
            )}
            <DayTotalsRow label={label} index={i} day={day} />
            <div className="mt-4" style={{ borderTop: '1px solid var(--line)' }}>
              {day.items.map((it) => (
                <ItemRow
                  key={[it.day_number, it.slot, it.component_order, it.etm_id].join('-')}
                  item={it}
                  planKey={planKey}
                  canAdjustSides={false}
                  canSwap={false}
                  submittingKey={null}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {planId && (
        <div className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}>
          Plan reference: <span data-testid={`plan-id-${planKey}`}>{planId}</span>
        </div>
      )}
    </section>
  );
}

export default function MealResults() {
  const { session, loading: sessionLoading, configured } = useSession();
  const nav = useNavigate();

  const [genPayload] = useState(() => readCachedGenerationResult());
  const genPlans = useMemo(() => readPlans(genPayload), [genPayload]);
  const genOutcome = useMemo(() => readGenOutcome(genPayload), [genPayload]);
  const genGenerationId = useMemo(() => readGenGenerationId(genPayload), [genPayload]);
  const isGenSuccess = genOutcome === 'generated' || genOutcome === 'replayed';

  const [loadingSelected, setLoadingSelected] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [busyAction, setBusyAction] = useState(false);
  const [submittingKey, setSubmittingKey] = useState(null);
  const [error, setError] = useState(null);
  const [showSwapFor, setShowSwapFor] = useState(null); // item currently being replaced

  // Authenticated resume: query get_selected_meal_plan once when the
  // session is established.  Never called before session is known.
  useEffect(() => {
    if (sessionLoading) return;
    if (!session) { setLoadingSelected(false); return; }
    let cancelled = false;
    setLoadingSelected(true);
    setError(null);
    rpcGetSelectedMealPlan().then((r) => {
      if (cancelled) return;
      if (r.ok && hasSelectedPlan(r.data)) {
        setSelectedPlan(readSelectedPlan(r.data));
      } else if (r.ok) {
        setSelectedPlan(null);
      } else {
        setError(r.error);
      }
      setLoadingSelected(false);
    });
    return () => { cancelled = true; };
  }, [session, sessionLoading]);

  async function handleSelect(planId, optionLabel) {
    if (!planId) return;
    setBusyAction(true); setError(null);
    const r = await rpcSelectMealPlan(planId);
    if (!r.ok) {
      setError(r.error); setBusyAction(false); return;
    }
    const outcome = readSelectedPlanOutcome(r.data);
    if (!isSuccessfulSelect(outcome)) {
      setError("We couldn't save that selection.");
      setBusyAction(false); return;
    }
    // Re-fetch the authoritative selected plan.
    const fresh = await rpcGetSelectedMealPlan();
    if (fresh.ok && hasSelectedPlan(fresh.data)) {
      setSelectedPlan(readSelectedPlan(fresh.data));
    }
    setBusyAction(false);
  }

  function handleDeselect() {
    // The customer wants to reconsider; keep the selected plan on the
    // server (they may come back).  Return them to the questionnaire's
    // preferences screen where "Build new options" is the recommended
    // action.  If a fresh generation payload is still cached, we'll
    // display those three options at the top of the page for a direct
    // switch.
    setSelectedPlan(null);
  }

  async function handleSwapPick(candidate) {
    if (!showSwapFor || !selectedPlan) return;
    const item = showSwapFor;
    const key = [item.day_number, item.slot, item.component_order, item.etm_id].join('-');
    setSubmittingKey(key); setError(null);
    const r = await rpcSwapMealPlanItem({
      plan_id: selectedPlan.plan_id,
      day_number: item.day_number,
      slot: item.slot,
      component_order: item.component_order,
      new_etm_id: candidate.etm_id,
    });
    if (!r.ok) {
      setError(r.error); setSubmittingKey(null); return;
    }
    const outcome = readSelectedPlanOutcome(r.data);
    if (!isSuccessfulSwap(outcome)) {
      if (outcome === 'stale_candidate') {
        setError('That replacement is no longer available.');
      } else {
        setError("We couldn't update this meal.");
      }
      setSubmittingKey(null);
      return;
    }
    // Apply the fresh affected day to the selected plan.
    const fresh = readAffectedDay(r.data);
    if (fresh) {
      setSelectedPlan((prev) => prev ? applyDay(prev, fresh) : prev);
    } else {
      // Full-plan replacement path.
      const p = readSelectedPlan(r.data);
      if (p && p.plan_id) setSelectedPlan(p);
    }
    setSubmittingKey(null);
    setShowSwapFor(null);
  }

  async function handleToggleSide(item) {
    if (!selectedPlan) return;
    const key = [item.day_number, item.slot, item.component_order, item.etm_id].join('-');
    const nextIncluded = item.customer_included === false; // toggle
    setSubmittingKey(key); setError(null);
    const r = await rpcSetMealPlanSideIncluded({
      plan_id: selectedPlan.plan_id,
      day_number: item.day_number,
      slot: item.slot,
      component_order: item.component_order,
      included: nextIncluded,
    });
    if (!r.ok) {
      setError(r.error); setSubmittingKey(null); return;
    }
    const outcome = readSelectedPlanOutcome(r.data);
    if (!isSuccessfulSideChange(outcome)) {
      setError("We couldn't save that change."); setSubmittingKey(null); return;
    }
    const fresh = readAffectedDay(r.data);
    if (fresh) {
      setSelectedPlan((prev) => prev ? applyDay(prev, fresh) : prev);
    } else {
      const p = readSelectedPlan(r.data);
      if (p && p.plan_id) setSelectedPlan(p);
    }
    setSubmittingKey(null);
  }

  function regenerate() {
    clearCachedGenerationResult();
    startNewGenerationAttempt();
    nav('/plan/generate');
  }
  function refine() {
    clearCachedGenerationResult();
    nav('/plan/preferences');
  }

  if (!configured) {
    return (
      <AppShell>
        <div className="wizard-card">
          <div className="eyebrow">Meal plan</div>
          <h1 className="h2 mt-3">Your plan is temporarily unavailable</h1>
          <p className="mt-4" style={{ color: 'var(--text-dim)' }}>Please try again in a moment.</p>
        </div>
      </AppShell>
    );
  }
  if (sessionLoading || loadingSelected) {
    return (
      <AppShell>
        <div className="wizard-card"><p style={{ color: 'var(--text-dim)' }}>Loading…</p></div>
      </AppShell>
    );
  }
  if (!session) return <Navigate to="/sign-in" replace state={{ returnTo: '/plan/results' }} />;

  // Generation-aware source-of-truth (Phase B5 fix):
  //   * If a fresh generation cache exists and its generation_id does
  //     not match the previously-selected plan's generation_id, then
  //     the selected plan belongs to an OLDER generation and MUST NOT
  //     mask the fresh options. Fall through to Branch B and render
  //     the fresh options; only after the customer selects one of
  //     these will select_meal_plan promote it to the new authoritative
  //     selection.
  //   * Otherwise (same generation_id, or no fresh cache), Branch A
  //     restores the persisted selection as normal.
  const hasFreshGeneration = !!genPayload && isGenSuccess && genPlans.length > 0;
  const freshGenIdDiffers =
    hasFreshGeneration &&
    genGenerationId &&
    selectedPlan &&
    selectedPlan.generation_id &&
    genGenerationId !== selectedPlan.generation_id;
  const showSelectedBranch = selectedPlan && !freshGenIdDiffers;

  // ---- Branch A: authoritative selected plan present ----
  if (showSelectedBranch) {
    return (
      <AppShell>
        <div className="wizard-card" data-testid="meal-results">
          <div className="eyebrow">Meal plan</div>
          <h1 className="h2 mt-3" data-testid="results-title">Your selected plan</h1>
          <p className="mt-3" style={{ color: 'var(--text-dim)' }}>
            Swap a meal or remove any optional side — your daily totals update
            after each change.
          </p>

          {error && (
            <div
              className="mt-4 p-3"
              style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}
              data-testid="results-error"
            >
              <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{error}</p>
            </div>
          )}

          <div className="mt-6" data-testid="plans-list">
            <SelectedPlanCard
              plan={selectedPlan}
              onDeselect={handleDeselect}
              onSwapItem={setShowSwapFor}
              onToggleSide={handleToggleSide}
              submittingKey={submittingKey}
            />
          </div>

          <div className="mt-6 flex gap-3 flex-wrap" data-testid="results-actions">
            <BrandButton type="button" variant="primary" onClick={() => nav('/plan/review')}
              data-testid="results-review-order-btn">Review order</BrandButton>
            <BrandButton type="button" variant="outline" onClick={regenerate}
              data-testid="results-regenerate-btn">Build new options</BrandButton>
            <BrandButton type="button" variant="ghost" onClick={refine}
              data-testid="results-adjust-preferences-btn">Back to preferences</BrandButton>
          </div>

          {selectedPlan.generation_id && (
            <div className="mt-6 text-xs" style={{ color: 'var(--text-dim)' }}>
              Generation reference: <span data-testid="results-generation-id">{selectedPlan.generation_id}</span>
              {Number.isFinite(selectedPlan.planner_rules_version) && (
                <> · planner rules v{selectedPlan.planner_rules_version}</>
              )}
            </div>
          )}
        </div>

        <SwapMealPicker
          open={!!showSwapFor}
          currentItem={showSwapFor}
          planId={selectedPlan.plan_id}
          generationId={selectedPlan.generation_id}
          submitting={submittingKey !== null}
          onCancel={() => setShowSwapFor(null)}
          onPick={handleSwapPick}
        />
      </AppShell>
    );
  }

  // ---- Branch B: unselected generated options (or empty state) ----
  if (!genPayload || genPlans.length === 0 || !isGenSuccess) {
    return (
      <AppShell>
        <div className="wizard-card" data-testid="results-empty">
          <div className="eyebrow">Meal plan</div>
          <h1 className="h2 mt-3">Let&rsquo;s build your plan</h1>
          <p className="mt-4" style={{ color: 'var(--text-dim)' }}>
            {genOutcome === 'no_eligible_meals'
              ? 'We could not find eligible meals for your current preferences. Try loosening a restriction and generating again.'
              : 'Head back to your planner so we can build three fresh options for you.'}
          </p>
          {error && (
            <p className="mt-4 text-sm" style={{ color: 'var(--danger, #b03a2e)' }}
              data-testid="results-error">{error}</p>
          )}
          <div className="mt-6 flex gap-3 flex-wrap">
            <BrandButton type="button" variant="primary" onClick={() => nav('/plan/generate')}
              data-testid="results-generate-btn">Build my plans</BrandButton>
            <BrandButton type="button" variant="outline" onClick={refine}
              data-testid="results-refine-btn">Refine my preferences</BrandButton>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="wizard-card" data-testid="meal-results">
        <div className="eyebrow">Meal plan</div>
        <h1 className="h2 mt-3" data-testid="results-title">Your three plan options</h1>
        <p className="mt-3" style={{ color: 'var(--text-dim)' }}>
          Every plan was built by our kitchen server for your goals. Pick the
          one that fits.
        </p>

        {error && (
          <div
            className="mt-4 p-3"
            style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}
            data-testid="results-error"
          >
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{error}</p>
          </div>
        )}

        <div className="mt-8" data-testid="plans-list">
          {genPlans.map((plan, i) => (
            <OptionPlanCard
              key={readPlanId(plan) || i}
              plan={plan} index={i}
              isSelecting={busyAction}
              onSelect={handleSelect}
            />
          ))}
        </div>

        <div className="mt-6 flex gap-3 flex-wrap" data-testid="results-actions">
          <BrandButton type="button" variant="outline" onClick={regenerate}
            data-testid="results-regenerate-btn">Build new options</BrandButton>
          <BrandButton type="button" variant="ghost" onClick={refine}
            data-testid="results-adjust-preferences-btn">Back to preferences</BrandButton>
        </div>

        {genGenerationId && (
          <div className="mt-6 text-xs" style={{ color: 'var(--text-dim)' }}>
            Generation reference: <span data-testid="results-generation-id">{genGenerationId}</span>
          </div>
        )}
      </div>
    </AppShell>
  );
}

// Replace a single day on the current selected plan with the fresh
// server-returned day (used after swap or side inclusion changes).
function applyDay(plan, freshDay) {
  const days = plan.days.map((d) => d.day_number === freshDay.day_number ? freshDay : d);
  return { ...plan, days };
}
